/**
 * CSV Import Progress Component
 *
 * Displays upload and processing progress with visual indicators.
 * Shows percentage, rows processed, and estimated time remaining.
 */

import { Loader2, Upload, Clock, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import type { ImportJobProgress, ImportJobStatus } from '@/types/user-migration';

interface CsvImportProgressProps {
  /** Current import state: 'uploading' or 'processing' */
  phase: 'uploading' | 'processing';
  /** Upload progress percentage (0-100) - used when phase is 'uploading' */
  uploadProgress?: number;
  /** Job progress data - used when phase is 'processing' */
  jobProgress?: ImportJobProgress | null;
  /** Current job status */
  jobStatus?: ImportJobStatus;
  /** Error message if applicable */
  error?: string | null;
  /** Whether the import was idempotent (file already imported) */
  isIdempotent?: boolean;
}

/**
 * Format seconds into human-readable time
 */
function formatTime(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Format number with commas
 */
function formatNumber(num: number): string {
  return num.toLocaleString();
}

/**
 * CSV Import Progress Component
 */
export function CsvImportProgress({
  phase,
  uploadProgress = 0,
  jobProgress,
  jobStatus,
  error,
  isIdempotent = false,
}: CsvImportProgressProps) {
  const isUploading = phase === 'uploading';
  const isProcessing = phase === 'processing';
  const hasError = !!error;

  // Calculate progress percentage
  const progressPercent = isUploading ? uploadProgress : (jobProgress?.percentComplete ?? 0);

  // Determine status for styling
  const isPending = jobStatus === 'pending';
  const isInProgress = jobStatus === 'processing';
  const isCompleted = jobStatus === 'completed';
  const isFailed = jobStatus === 'failed';
  const isCancelled = jobStatus === 'cancelled';

  return (
    <div className="space-y-4">
      {/* Status Header */}
      <div className="flex items-center gap-3">
        {isUploading && (
          <>
            <Upload className="h-5 w-5 text-primary animate-pulse" aria-hidden="true" />
            <span className="font-medium">Uploading file...</span>
          </>
        )}

        {isProcessing && isPending && (
          <>
            <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" aria-hidden="true" />
            <span className="font-medium text-muted-foreground">Waiting to start...</span>
          </>
        )}

        {isProcessing && isInProgress && (
          <>
            <Loader2 className="h-5 w-5 text-primary animate-spin" aria-hidden="true" />
            <span className="font-medium">Processing import...</span>
          </>
        )}

        {isProcessing && isCompleted && (
          <>
            <CheckCircle2 className="h-5 w-5 text-green-600" aria-hidden="true" />
            <span className="font-medium text-green-600">Import completed</span>
          </>
        )}

        {isProcessing && isFailed && (
          <>
            <XCircle className="h-5 w-5 text-destructive" aria-hidden="true" />
            <span className="font-medium text-destructive">Import failed</span>
          </>
        )}

        {isProcessing && isCancelled && (
          <>
            <AlertCircle className="h-5 w-5 text-yellow-600" aria-hidden="true" />
            <span className="font-medium text-yellow-600">Import cancelled</span>
          </>
        )}
      </div>

      {/* Idempotent Notice */}
      {isIdempotent && (
        <div
          className="text-sm text-muted-foreground bg-muted/50 rounded-md px-3 py-2"
          role="status"
        >
          This file was previously imported. Showing existing job status.
        </div>
      )}

      {/* Progress Bar */}
      <div className="space-y-2">
        <Progress
          value={progressPercent}
          className={cn(
            'h-2',
            hasError && 'bg-destructive/20',
            isCompleted && 'bg-green-100',
            isCancelled && 'bg-yellow-100'
          )}
          aria-label={isUploading ? 'Upload progress' : 'Processing progress'}
        />
        <div className="flex justify-between text-sm text-muted-foreground">
          <span aria-live="polite">{progressPercent}% complete</span>
          {isProcessing && jobProgress?.estimatedSecondsRemaining != null && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" aria-hidden="true" />
              <span>~{formatTime(jobProgress.estimatedSecondsRemaining)} remaining</span>
            </span>
          )}
        </div>
      </div>

      {/* Processing Stats */}
      {isProcessing && jobProgress && (
        <div
          className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm"
          role="region"
          aria-label="Import statistics"
        >
          <div className="bg-muted/50 rounded-md px-3 py-2">
            <div className="text-muted-foreground">Total Rows</div>
            <div className="font-medium">{formatNumber(jobProgress.totalRows)}</div>
          </div>
          <div className="bg-muted/50 rounded-md px-3 py-2">
            <div className="text-muted-foreground">Processed</div>
            <div className="font-medium">{formatNumber(jobProgress.processedRows)}</div>
          </div>
          <div className="bg-green-50 dark:bg-green-950/20 rounded-md px-3 py-2">
            <div className="text-green-700 dark:text-green-400">Created</div>
            <div className="font-medium text-green-700 dark:text-green-400">
              {formatNumber(jobProgress.createdRows)}
            </div>
          </div>
          <div className="bg-muted/50 rounded-md px-3 py-2">
            <div className="text-muted-foreground">Skipped</div>
            <div className="font-medium">{formatNumber(jobProgress.skippedRows)}</div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {hasError && (
        <div
          className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2"
          role="alert"
          aria-live="assertive"
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
